/**
 * MockDataTab
 *
 * NodeEditor tab that lets the user inspect and edit the per-node mock state
 * stored in `workflowStore.mockedNodes`. The same component handles both
 * the trigger entry (a bare `TriggerMockPayload`) and per-action entries
 * (`MockNodeInfo` with `mockOutput`).
 *
 * The values shown here are exactly what `useTriggerMockRun` ships to the
 * `submitDraftRun` API — edits made here are visible in the next mock run.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dropdown,
  Field,
  Input,
  Option,
  Switch,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { JsonEditor } from '@/components/WorkflowDesigner/JsonEditor/JsonEditor';
import { useWorkflowStore } from '@/stores/workflowStore';
import type {
  MockDefinition,
  MockNodeInfo,
  TriggerMockPayload,
} from '@/types/templateMode';

const MOCK_STATUSES = ['Succeeded', 'Failed', 'Skipped', 'TimedOut', 'Cancelled'] as const;
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'] as const;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    flex: 1,
    minHeight: 0,
  },
  row: {
    display: 'flex',
    columnGap: tokens.spacingHorizontalM,
    alignItems: 'flex-end',
  },
  rowItem: {
    flex: 1,
    minWidth: 0,
  },
  jsonField: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: '120px',
  },
  jsonFieldSmall: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '200px',
  },
  jsonContainer: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
  },
  helper: {
    color: tokens.colorNeutralForeground3,
  },
  toggleRow: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

// =============================================================================
// Helpers
// =============================================================================

/** Parse JSON; return undefined on failure so the previous value is kept. */
function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** Render undefined as blank editor text; preserve explicit null as "null". */
function toEditorJson(value: unknown): string {
  if (value === undefined) return '';
  return JSON.stringify(value, null, 2);
}

function emptyMockOutput(): MockDefinition {
  return { status: 'Succeeded', outputs: { statusCode: '200' } };
}

// =============================================================================
// MockDataTab
// =============================================================================

export interface MockDataTabProps {
  /** Canvas-prefixed node id (`trigger-<name>` or `action-<scoped>`). */
  nodeId: string;
}

export function MockDataTab({ nodeId }: MockDataTabProps): JSX.Element {
  const entry = useWorkflowStore((s) => s.mockedNodes[nodeId]);
  const setMockedNode = useWorkflowStore((s) => s.setMockedNode);
  const setMockNodeEnabled = useWorkflowStore((s) => s.setMockNodeEnabled);

  if (nodeId.startsWith('trigger-')) {
    return (
      <TriggerMockForm
        nodeId={nodeId}
        entry={entry as TriggerMockPayload | undefined}
        onChange={setMockedNode}
        onToggleEnabled={setMockNodeEnabled}
      />
    );
  }
  return (
    <ActionMockForm
      nodeId={nodeId}
      entry={entry as MockNodeInfo | undefined}
      onChange={setMockedNode}
      onToggleEnabled={setMockNodeEnabled}
    />
  );
}

// =============================================================================
// Action editor
// =============================================================================

interface ActionMockFormProps {
  nodeId: string;
  entry: MockNodeInfo | undefined;
  onChange: (nodeId: string, info: MockNodeInfo) => void;
  onToggleEnabled: (nodeId: string, enabled: boolean) => void;
}

function ActionMockForm({ nodeId, entry, onChange, onToggleEnabled }: ActionMockFormProps): JSX.Element {
  const styles = useStyles();
  const actionName = entry?.actionName ?? nodeId.replace(/^action-/, '');
  const enabled = entry?.enabled !== false;
  const mockOutput = useMemo<MockDefinition>(
    () => entry?.mockOutput ?? emptyMockOutput(),
    [entry?.mockOutput],
  );

  // Buffer the JSON text so the user can type freely without partial-JSON
  // errors clobbering their input; commit on each edit if the text parses.
  const [bodyText, setBodyText] = useState(() => toEditorJson(mockOutput.outputs.body));
  const [headersText, setHeadersText] = useState(() => JSON.stringify(mockOutput.outputs.headers ?? {}, null, 2));

  // Reset the local buffers when the node changes; intentionally NOT a dep on
  // the mockOutput so external edits don't fight a user mid-keystroke.
  useEffect(() => {
    setBodyText(toEditorJson(mockOutput.outputs.body));
    setHeadersText(JSON.stringify(mockOutput.outputs.headers ?? {}, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  const writeMock = useCallback(
    (next: MockDefinition) => {
      onChange(nodeId, {
        actionName,
        mockOutput: next,
        // Preserve the per-action toggle across edits.
        ...(entry?.enabled !== undefined ? { enabled: entry.enabled } : {}),
      });
    },
    [nodeId, actionName, onChange, entry?.enabled],
  );

  const handleStatusChange = useCallback(
    (status: string) => writeMock({ ...mockOutput, status }),
    [mockOutput, writeMock],
  );

  const handleStatusCodeChange = useCallback(
    (statusCode: string) =>
      writeMock({ ...mockOutput, outputs: { ...mockOutput.outputs, statusCode } }),
    [mockOutput, writeMock],
  );

  const handleBodyChange = useCallback(
    (text: string) => {
      setBodyText(text);
      if (text.trim() === '') {
        const { body: _ignored, ...restOutputs } = mockOutput.outputs;
        writeMock({ ...mockOutput, outputs: restOutputs });
        return;
      }
      const parsed = safeParse(text);
      if (parsed !== undefined) {
        writeMock({ ...mockOutput, outputs: { ...mockOutput.outputs, body: parsed } });
      }
    },
    [mockOutput, writeMock],
  );

  const handleHeadersChange = useCallback(
    (text: string) => {
      setHeadersText(text);
      const parsed = safeParse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        writeMock({
          ...mockOutput,
          outputs: { ...mockOutput.outputs, headers: parsed as Record<string, string> },
        });
      }
    },
    [mockOutput, writeMock],
  );

  return (
    <div className={styles.root}>
      <div className={styles.toggleRow}>
        <Switch
          checked={enabled}
          label={enabled ? 'Test data on' : 'Test data off'}
          onChange={(_, data) => onToggleEnabled(nodeId, data.checked)}
        />
        <Text className={styles.helper}>
          When on, this action returns the test output below instead of running
          live. When off, it runs normally and shows its connection status.
        </Text>
      </div>
      <div className={styles.row}>
        <Field label="Status" className={styles.rowItem}>
          <Dropdown
            value={mockOutput.status}
            selectedOptions={[mockOutput.status]}
            onOptionSelect={(_, data) => {
              if (data.optionValue) handleStatusChange(data.optionValue);
            }}
          >
            {MOCK_STATUSES.map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Dropdown>
        </Field>
        <Field label="Status code" className={styles.rowItem}>
          <Input
            value={mockOutput.outputs.statusCode ?? ''}
            onChange={(_, data) => handleStatusCodeChange(data.value)}
          />
        </Field>
      </div>

      <Field label="Body (JSON)" className={styles.jsonField}>
        <div className={styles.jsonContainer}>
          <JsonEditor value={bodyText} onChange={handleBodyChange} fill />
        </div>
      </Field>

      <Field label="Headers (JSON)" className={styles.jsonFieldSmall}>
        <div className={styles.jsonContainer}>
          <JsonEditor value={headersText} onChange={handleHeadersChange} fill />
        </div>
      </Field>
    </div>
  );
}

// =============================================================================
// Trigger editor
// =============================================================================

interface TriggerMockFormProps {
  nodeId: string;
  entry: TriggerMockPayload | undefined;
  onChange: (nodeId: string, info: TriggerMockPayload) => void;
  onToggleEnabled: (nodeId: string, enabled: boolean) => void;
}

function TriggerMockForm({ nodeId, entry, onChange, onToggleEnabled }: TriggerMockFormProps): JSX.Element {
  const styles = useStyles();
  const method = entry?.method ?? 'POST';
  const enabled = entry?.enabled !== false;

  const [bodyText, setBodyText] = useState(() => toEditorJson(entry?.body));
  const [headersText, setHeadersText] = useState(() => JSON.stringify(entry?.headers ?? {}, null, 2));
  const [queriesText, setQueriesText] = useState(() => JSON.stringify(entry?.queries ?? {}, null, 2));

  useEffect(() => {
    setBodyText(toEditorJson(entry?.body));
    setHeadersText(JSON.stringify(entry?.headers ?? {}, null, 2));
    setQueriesText(JSON.stringify(entry?.queries ?? {}, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  const write = useCallback(
    (next: TriggerMockPayload) => onChange(nodeId, next),
    [nodeId, onChange],
  );

  const handleMethodChange = useCallback(
    (next: string) => {
      if (entry?.body === undefined) {
        write({ ...entry, method: next });
        return;
      }
      write({ ...entry, method: next, body: entry.body });
    },
    [entry, write],
  );

  const handleHeadersChange = useCallback(
    (text: string) => {
      setHeadersText(text);
      const parsed = safeParse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        write({ ...entry, method, headers: parsed as Record<string, string> });
      }
    },
    [entry, method, write],
  );

  const handleQueriesChange = useCallback(
    (text: string) => {
      setQueriesText(text);
      const parsed = safeParse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        write({ ...entry, method, queries: parsed as Record<string, string> });
      }
    },
    [entry, method, write],
  );

  const handleBodyChange = useCallback(
    (text: string) => {
      setBodyText(text);
      if (text.trim() === '') {
        const { body: _ignored, ...rest } = (entry ?? { method }) as TriggerMockPayload;
        write({ ...rest, method });
        return;
      }
      const parsed = safeParse(text);
      if (parsed !== undefined) {
        write({ ...entry, method, body: parsed });
      }
    },
    [entry, method, write],
  );

  return (
    <div className={styles.root}>
      <div className={styles.toggleRow}>
        <Switch
          checked={enabled}
          label={enabled ? 'Mock data on' : 'Mock data off'}
          onChange={(_, data) => onToggleEnabled(nodeId, data.checked)}
        />
        <Text className={styles.helper}>
          When on, this sample is sent to the trigger when running with mocks.
          When off, the dialog uses the stored sample payload instead.
        </Text>
      </div>

      <Field label="Method">
        <Dropdown
          value={method}
          selectedOptions={[method]}
          onOptionSelect={(_, data) => {
            if (data.optionValue) handleMethodChange(data.optionValue);
          }}
        >
          {HTTP_METHODS.map((m) => (
            <Option key={m} value={m}>
              {m}
            </Option>
          ))}
        </Dropdown>
      </Field>

      <Field label="Body (JSON)" className={styles.jsonField}>
        <div className={styles.jsonContainer}>
          <JsonEditor value={bodyText} onChange={handleBodyChange} fill />
        </div>
      </Field>

      <Field label="Headers (JSON)" className={styles.jsonFieldSmall}>
        <div className={styles.jsonContainer}>
          <JsonEditor value={headersText} onChange={handleHeadersChange} fill />
        </div>
      </Field>

      <Field label="Query parameters (JSON)" className={styles.jsonFieldSmall}>
        <div className={styles.jsonContainer}>
          <JsonEditor value={queriesText} onChange={handleQueriesChange} fill />
        </div>
      </Field>
    </div>
  );
}
